import { Component, Input, OnInit } from '@angular/core';
import { AppConfig } from '@app/app.config';
import { COUNTRIES, StateCode, STATES, CANADA_PROVINCES } from '@shared/components/address-list/address-list-form.interface';
import { CountryReport, SalesTaxReport, StateReport } from '@shared/meta-data/sales-tax-report.meta';
import { CountryCodeName } from '@shared/components/country-tax-card/country-tax-card.component';

@Component({
  selector: 'app-insights-board',
  templateUrl: './insights-board.component.html',
})
export class InsightsBoardComponent implements OnInit {

  @Input() salesTaxReport: SalesTaxReport

  statesWithoutNexus: CodeName[] = []
  statesWithNexusAndAutoFilling: CodeName[] = []
  statesWithNexusAndManualFilling: CodeName[] = []
  selectedStates: string[] = [];
  selectedCountries: string[] = [];
  activeTab: 'states' | 'countries' = 'states';

  countriesWithNexus: CountryCodeName[] = [];
  countriesWithNexusAndAutoFilling: CountryCodeName[] = [];
  countriesWithNexusAndManualFilling: CountryCodeName[] = [];
  countriesWithoutNexus: CountryCodeName[] = [];

  reconReportURL: string = AppConfig.API_URL + '/sales-tax-report/reconciliation/state/export'

  get periodStart(): string {
    return this.salesTaxReport?.summary?.date?.from;
  }

  get periodEnd(): string {
    return this.salesTaxReport?.summary?.date?.to;
  }

  ngOnInit(): void {
    this.initialize()
  }


  initialize() {
    this.initializeStates();
    this.initializeCountries();
  }

  initializeStates() {
    this.statesWithoutNexus = [];
    this.statesWithNexusAndAutoFilling = [];
    this.statesWithNexusAndManualFilling = [];

    const breakdown = Object.entries(this.salesTaxReport.breakdown || {}).sort(this.#reOrderStatesByTaxesDue);

    for (const [key, entries] of breakdown) {
      if (entries.has_nexus) {
        if (entries.enrolled_in_autofilling) {
          this.statesWithNexusAndAutoFilling.push({ name: STATES[key], code: key as StateCode, ...entries });
        } else {
          this.statesWithNexusAndManualFilling.push({ name: STATES[key], code: key as StateCode, ...entries });
        }
      } else {
        this.statesWithoutNexus.push({ name: STATES[key], code: key as StateCode, ...entries });
      }
    }
  }

  initializeCountries(filterKeys?: string[]) {
    this.countriesWithNexus = [];
    this.countriesWithNexusAndAutoFilling = [];
    this.countriesWithNexusAndManualFilling = [];
    this.countriesWithoutNexus = [];

    const international = this.salesTaxReport.international_breakdown;
    if (!international) return;

    let entries = Object.entries(international);
    if (filterKeys?.length) {
      entries = entries.filter(([key]) => this.#matchesCountryFilter(key, filterKeys));
    }

    entries.sort(this.#reOrderCountriesByTaxesDue);
    for (const [key, report] of entries) {
      const region = (report as CountryReport & { region_code?: string }).region_code;
      const countryCode = (report as CountryReport & { country_code?: string }).country_code || key.split('-')[0];
      const card: CountryCodeName = {
        ...report,
        code: key,
        name: (report as CountryReport & { jurisdiction_label?: string }).jurisdiction_label
          || (region ? CANADA_PROVINCES[region]?.ui_label : null)
          || (COUNTRIES as Record<string, string>)[countryCode]
          || key,
      };
      if (report.has_nexus) {
        this.countriesWithNexus.push(card);
        if (report.enrolled_in_autofilling) {
          this.countriesWithNexusAndAutoFilling.push(card);
        } else {
          this.countriesWithNexusAndManualFilling.push(card);
        }
      } else {
        this.countriesWithoutNexus.push(card);
      }
    }
  }

  // sort by total sales tax due
  #reOrderStatesByTaxesDue([,a], [,b]): any {
    return b?.reports?.total_sales_tax_due - a?.reports?.total_sales_tax_due;
  }

  #reOrderCountriesByTaxesDue([, a]: [string, CountryReport], [, b]: [string, CountryReport]): number {
    const taxA = Number(a?.reports?.total_sales_tax ?? a?.reports?.country_tax ?? a?.reports?.total_sales_tax_due ?? 0);
    const taxB = Number(b?.reports?.total_sales_tax ?? b?.reports?.country_tax ?? b?.reports?.total_sales_tax_due ?? 0);
    return taxB - taxA;
  }

  get hasStateContent(): boolean {
    return this.statesWithNexusAndAutoFilling.length > 0
      || this.statesWithNexusAndManualFilling.length > 0
      || this.statesWithoutNexus.length > 0;
  }

  get hasCountryContent(): boolean {
    return this.countriesWithNexusAndAutoFilling.length > 0
      || this.countriesWithNexusAndManualFilling.length > 0
      || this.countriesWithoutNexus.length > 0;
  }

  setTab(tab: 'states' | 'countries') {
    this.activeTab = tab;
  }

  filterByCountry(countriesToFilter: string[]) {
    this.selectedCountries = countriesToFilter;
    this.initializeCountries(countriesToFilter.length ? countriesToFilter : undefined);
  }

  #matchesCountryFilter(key: string, filters: string[]): boolean {
    return filters.some((filter) => {
      if (key === filter) return true;
      if (filter.includes('-')) return false;
      return key.startsWith(`${filter}-`);
    });
  }

  filterByState(statesToFilter: string[]){
    this.selectedStates = statesToFilter;

    this.statesWithoutNexus = [];
    this.statesWithNexusAndAutoFilling = [];
    this.statesWithNexusAndManualFilling = [];

    if (!this.salesTaxReport.breakdown) return;

    if(this.selectedStates.length === 0) {
      this.initializeStates();
      this.initializeCountries(this.selectedCountries.length ? this.selectedCountries : undefined);
      return;
    }

    const breakdown = Object.entries(this.salesTaxReport.breakdown).filter(([key]) => this.selectedStates.includes(key)).sort(this.#reOrderStatesByTaxesDue);
    
    for (let [key, entries] of breakdown)
      if (entries.has_nexus) {
        if (entries.enrolled_in_autofilling) {
          this.statesWithNexusAndAutoFilling.push({ name: STATES[key], code: key as StateCode, ...entries })
        } else {
          this.statesWithNexusAndManualFilling.push({ name: STATES[key], code: key as StateCode, ...entries })
        }
      } else {
        this.statesWithoutNexus.push({ name: STATES[key], code: key as StateCode, ...entries })
      }

    // Keep international cards visible; preserve any active country filter.
    this.initializeCountries(this.selectedCountries.length ? this.selectedCountries : undefined);
  }
}

interface CodeName extends StateReport {
  code: StateCode;
  name: STATES;
}
